import { useState } from "react";
import { ChevronDown, Map, Music, CalendarDays, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Progress } from "@/components/ui/progress";
import { describeProductionProgress, type AdminProduction } from "@produce/shared";
import CrewInviteTable from "./CrewInviteTable";
import FactGrid from "./FactGrid";
import { formatDay, formatStamp } from "./format";

/**
 * One production, from an admin's point of view: how far through the wizard it
 * is, how big it is, and who was invited.
 *
 * The step name comes from the shared `PRODUCTION_STEPS` list the editor uses,
 * so "step 4 of 9" always names the screen the producer is actually looking at.
 */
export default function ProductionCard({ production }: { production: AdminProduction }) {
  // Controlled rather than letting Collapsible own its state: the label has to
  // read "Hide crew" once it is open, and a CSS-only swap would leave a screen
  // reader announcing both.
  const [showCrew, setShowCrew] = useState(false);
  const progress = describeProductionProgress(production.status, production.last_edit_step);

  return (
    <div className="rounded-lg border bg-background p-4">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h4 className="font-semibold">{production.festival_name || "Untitled production"}</h4>
          <p className="text-sm text-muted-foreground">
            {formatDay(production.festival_date_start)}
            {production.festival_date_end &&
              production.festival_date_end !== production.festival_date_start &&
              ` – ${formatDay(production.festival_date_end)}`}
            {production.day_count > 0 &&
              ` · ${production.day_count} day${production.day_count === 1 ? "" : "s"}`}
          </p>
        </div>
        <Badge variant={progress.isPublished ? "default" : "secondary"}>{production.status}</Badge>
      </div>

      {!progress.isPublished && (
        <div className="mt-3">
          <div className="mb-1 flex items-center justify-between text-sm">
            <span className="text-muted-foreground">
              Step {progress.stepNumber} of {progress.stepCount} — {progress.stepLabel}
            </span>
            <span className="text-muted-foreground">{progress.percentComplete}%</span>
          </div>
          <Progress value={progress.percentComplete} className="h-2" />
        </div>
      )}

      <div className="mt-4">
        <FactGrid
          facts={[
            {
              label: "Stages",
              value: (
                <span className="flex items-center gap-1">
                  <CalendarDays className="h-3.5 w-3.5 text-muted-foreground" />
                  {production.stage_names.length > 0
                    ? production.stage_names.join(", ")
                    : "None yet"}
                </span>
              ),
            },
            {
              label: "Lineup",
              value: (
                <span className="flex items-center gap-1">
                  <Music className="h-3.5 w-3.5 text-muted-foreground" />
                  {production.lineup_count} act{production.lineup_count === 1 ? "" : "s"}
                </span>
              ),
            },
            {
              label: "Assignments",
              value: `${production.assignment_count}`,
            },
            {
              label: "Festival map",
              value: (
                <span className="flex items-center gap-1">
                  <Map className="h-3.5 w-3.5 text-muted-foreground" />
                  {production.has_map ? "Uploaded" : "None"}
                </span>
              ),
            },
            { label: "Created", value: formatDay(production.created_at) },
            { label: "Last edited", value: formatStamp(production.updated_at) },
            {
              label: "Published",
              value: production.published_at ? formatStamp(production.published_at) : "—",
            },
            {
              label: "Crew invited",
              value: (
                <span className="flex items-center gap-1">
                  <Users className="h-3.5 w-3.5 text-muted-foreground" />
                  {production.crew.length}
                </span>
              ),
            },
          ]}
        />
      </div>

      {/* Radix Collapsible rather than a hand-rolled toggle: it wires up
          aria-expanded / aria-controls, and it is what the rest of the app
          uses for a disclosure (see briefing/FlightsCard). */}
      <Collapsible open={showCrew} onOpenChange={setShowCrew}>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="sm" className="group mt-3 px-0">
            <ChevronDown className="mr-1 h-4 w-4 transition-transform group-data-[state=open]:rotate-180" />
            {showCrew ? "Hide" : "Show"} crew ({production.crew.length})
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent className="mt-3">
          <CrewInviteTable crew={production.crew} />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
}
